import {
  Datagrid,
  TextField,
  FunctionField,
  SimpleList,
  useListContext,
  type RaRecord,
} from "react-admin";
import { Typography, useMediaQuery, useTheme, Box, Button } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { formatDisplayDateTime } from "@/utils/date-format";
import { RecipientStatusChip } from "./StatusChips";
import { describeRecipientListSecondaryText } from "./recipient-list-secondary-text";
import RecipientStatusTiles from "./RecipientStatusTiles";

interface RecipientsListContentProps {
  broadcastRecord?: Record<string, unknown> | null;
}

const RecipientsListContent = ({ broadcastRecord }: RecipientsListContentProps = {}) => {
  const { isLoading, error, data, filterValues, setFilters } = useListContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const activeStatus = (filterValues?.status as string | undefined)?.toUpperCase();

  const handleClearStatusFilter = () => {
    const nextFilters = { ...filterValues };
    delete nextFilters.status;
    setFilters(nextFilters, undefined, false);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <RecipientStatusTiles broadcastRecord={broadcastRecord} />

      {error ? (
        <Typography color="error" sx={{ my: 2, px: { xs: 1.5, sm: 2, md: 3 } }}>
          Failed to load recipients: {(error as Error).message}
        </Typography>
      ) : isLoading ? (
        <Typography sx={{ my: 2, px: { xs: 1.5, sm: 2, md: 3 } }}>Loading recipients...</Typography>
      ) : !data || data.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 4, px: { xs: 1.5, sm: 2, md: 3 } }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {activeStatus
              ? `No recipients found with status "${activeStatus}".`
              : "No recipients found for this broadcast."}
          </Typography>
          {activeStatus && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<CloseIcon />}
              onClick={handleClearStatusFilter}
              sx={{ mt: 1 }}
            >
              Show all recipients
            </Button>
          )}
        </Box>
      ) : isMobile ? (
        <SimpleList
          primaryText={(recipient) =>
            String(recipient.recipientName ?? recipient.recipientPhone ?? "—")
          }
          secondaryText={(recipient) => describeRecipientListSecondaryText(recipient)}
          tertiaryText={(recipient) => <RecipientStatusChip status={recipient.status as string} />}
        />
      ) : (
        <Datagrid bulkActionButtons={false}>
          <TextField source="recipientName" label="Name" />
          <TextField source="recipientPhone" label="Phone" />
          <FunctionField
            label="Status"
            render={(recipient: RaRecord) => (
              <RecipientStatusChip status={recipient.status as string} />
            )}
          />
          <FunctionField
            label="Sent"
            sortBy="sentAt"
            render={(recipient: RaRecord) =>
              formatDisplayDateTime(recipient?.sentAt, { empty: "—" })
            }
          />
          <FunctionField
            label="Delivered"
            sortBy="deliveredAt"
            render={(recipient: RaRecord) =>
              formatDisplayDateTime(recipient?.deliveredAt, { empty: "—" })
            }
          />
          <FunctionField
            label="Read"
            sortBy="readAt"
            render={(recipient: RaRecord) =>
              formatDisplayDateTime(recipient?.readAt, { empty: "—" })
            }
          />
          <TextField source="errorMessage" label="Error" />
        </Datagrid>
      )}
    </Box>
  );
};

export default RecipientsListContent;
